/**
 * Unified Cart Manager
 *
 * Centralized cart management system to prevent conflicts between
 * different cart-related scripts (Nomination validation, Engraving, etc.)
 */

class CartManager {
  constructor() {
    this.listeners = new Map();
    this.isInitialized = false;
    this.cartCache = null;
    this.cacheTimestamp = 0;
    this.cacheTimeout = 5000; // 5 seconds
    this.isProcessing = false;
    this.updateTimeout = null;
    this.updateRateLimit = null;

    // Prevent multiple instances
    if (window.CartManager) {
      return window.CartManager;
    }

    this.init();
    window.CartManager = this;
  }

  init() {
    if (this.isInitialized) return;

    this.setupFetchMonitoring();
    this.setupEventSystem();
    this.isInitialized = true;
  }

  /**
   * Setup fetch monitoring (single instance)
   */
  setupFetchMonitoring() {
    if (window._cartManagerFetchPatched) return;

    window._cartManagerFetchPatched = true;
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      // Check if this was a cart API call
      const url = args[0];
      if (typeof url === 'string' && this.isCartApiCall(url)) {
        // Extract request body for removal detection
        let requestBody = null;
        if (args[1] && args[1].body) {
          try {
            requestBody = JSON.parse(args[1].body);
          } catch (e) {
            // If not JSON, treat as string
            requestBody = args[1].body;
          }
        }

        this.handleCartApiCall(url, response, requestBody);
      }

      return response;
    };
  }

  /**
   * Check if URL is a cart API call
   */
  isCartApiCall(url) {
    return (
      url.includes('/cart.js') ||
      url.includes('/cart/add') ||
      url.includes('/cart/change') ||
      url.includes('/cart/update') ||
      url.includes('/cart/clear')
    );
  }

  /**
   * Handle cart API calls
   */
  async handleCartApiCall(url, response, requestBody = null) {
    // Prevent infinite loops by checking if we're already processing
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    try {
      // Clear cache on any cart modification
      this.clearCache();

      // Notify all listeners
      this.notifyListeners('cart:api-call', { url, response, requestBody });

      // If it was a successful modification, trigger cart updated
      if (response.ok && this.isCartModification(url)) {
        // Check if this was a removal operation
        const isRemoval = this.isCartRemoval(url, requestBody);

        // Use a single timeout to prevent multiple rapid calls
        if (this.updateTimeout) {
          clearTimeout(this.updateTimeout);
        }

        this.updateTimeout = setTimeout(() => {
          this.notifyListeners('cart:updated');

          // If it was a removal operation, refresh the page after a short delay
          if (isRemoval) {
            setTimeout(() => {
              // Only refresh if we're on the cart page
              if (window.location.pathname.includes('/cart')) {
                window.location.reload();
              }
            }, 500);
          }

          this.isProcessing = false;
        }, 300); // Increased delay to prevent race conditions
      } else {
        this.isProcessing = false;
      }
    } catch (error) {
      console.error('Error handling cart API call:', error);
      this.isProcessing = false;
    }
  }

  /**
   * Check if URL is a cart modification
   */
  isCartModification(url) {
    return (
      url.includes('/cart/add') ||
      url.includes('/cart/change') ||
      url.includes('/cart/update') ||
      url.includes('/cart/clear')
    );
  }

  /**
   * Check if URL is a cart removal operation
   */
  isCartRemoval(url, requestBody = null) {
    // Check if the URL contains quantity=0 or is a removal operation
    const urlRemoval =
      (url.includes('/cart/change') && (url.includes('quantity=0') || url.includes('quantity%3D0'))) ||
      url.includes('/cart/clear');

    // Check if request body indicates removal
    let bodyRemoval = false;
    if (requestBody) {
      if (typeof requestBody === 'object') {
        // JSON body - check for quantity: 0
        bodyRemoval =
          requestBody.quantity === 0 || (Array.isArray(requestBody) && requestBody.some((item) => item.quantity === 0));
      } else if (typeof requestBody === 'string') {
        // String body - check for quantity=0
        bodyRemoval = requestBody.includes('quantity=0') || requestBody.includes('quantity%3D0');
      }
    }

    return urlRemoval || bodyRemoval;
  }

  /**
   * Setup event system
   */
  setupEventSystem() {
    // Listen for DOM changes that might indicate cart updates
    const observer = new MutationObserver((mutations) => {
      let shouldNotify = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Check if cart items were added/removed
          const hasCartChanges =
            Array.from(mutation.addedNodes).some(
              (node) =>
                node.nodeType === Node.ELEMENT_NODE &&
                (node.matches?.('.cart-item') || node.querySelector?.('.cart-item'))
            ) ||
            Array.from(mutation.removedNodes).some(
              (node) =>
                node.nodeType === Node.ELEMENT_NODE &&
                (node.matches?.('.cart-item') || node.querySelector?.('.cart-item'))
            );

          if (hasCartChanges) {
            shouldNotify = true;
          }
        }
      });

      if (shouldNotify) {
        this.notifyListeners('cart:dom-changed');
      }
    });

    // Observe cart containers
    const cartContainers = document.querySelectorAll(
      '#main-cart-items, #CartDrawer-CartItems, .cart-items, .cart__contents'
    );

    cartContainers.forEach((container) => {
      observer.observe(container, {
        childList: true,
        subtree: true,
      });
    });

    // Listen for form submissions
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (this.isCartForm(form)) {
        this.notifyListeners('cart:form-submit', { form });
      }
    });

    // Listen for quantity changes
    document.addEventListener('input', (event) => {
      if (event.target.matches('input[name="updates[]"], input[name="quantity"]')) {
        this.notifyListeners('cart:quantity-change', {
          target: event.target,
          value: event.target.value,
        });
      }
    });

    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name="updates[]"], input[name="quantity"]')) {
        this.notifyListeners('cart:quantity-change', {
          target: event.target,
          value: event.target.value,
        });
      }
    });

    // Listen for remove button clicks - enhanced for cart drawer
    document.addEventListener('click', (event) => {
      const removeSelectors = [
        'cart-remove-button',
        'a[href*="remove"]',
        'button[data-cart-remove]',
        '.cart-remove',
        '[data-cart-remove]',
        '.btn--remove',
        '.cart__remove',
        '[href*="quantity=0"]',
        'button[data-cart-remove]',
        '.cart-item__remove',
        '.line-item__remove',
        'cart-remove-button',
        '[data-cart-item-key] button[type="button"]',
        '.cart-drawer .cart-remove',
        '.cart-drawer [data-cart-remove]',
        '#CartDrawer .cart-remove',
        '#CartDrawer [data-cart-remove]',
      ];

      if (event.target.closest(removeSelectors.join(', '))) {
        this.notifyListeners('cart:item-remove', { target: event.target });
      }
    });
  }

  /**
   * Check if form is a cart form
   */
  isCartForm(form) {
    return form.matches('#cart, #CartDrawer-Form, form[action*="/cart"]');
  }

  /**
   * Register a listener for cart events
   */
  on(event, callback, context = null) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }

    this.listeners.get(event).push({ callback, context });

    // Return unsubscribe function
    return () => this.off(event, callback, context);
  }

  /**
   * Remove a listener
   */
  off(event, callback, context = null) {
    if (!this.listeners.has(event)) return;

    const eventListeners = this.listeners.get(event);
    const index = eventListeners.findIndex(
      (listener) => listener.callback === callback && listener.context === context
    );

    if (index > -1) {
      eventListeners.splice(index, 1);
    }
  }

  /**
   * Notify all listeners of an event with rate limiting
   */
  notifyListeners(event, data = {}) {
    if (!this.listeners.has(event)) return;

    // Rate limiting for cart:updated events
    if (event === 'cart:updated') {
      if (this.updateRateLimit) {
        clearTimeout(this.updateRateLimit);
      }

      this.updateRateLimit = setTimeout(() => {
        this._notifyListenersInternal(event, data);
      }, 200); // Increased delay to prevent rapid fire events
      return;
    }

    this._notifyListenersInternal(event, data);
  }

  /**
   * Internal method to notify listeners
   */
  _notifyListenersInternal(event, data = {}) {
    const eventListeners = this.listeners.get(event);
    eventListeners.forEach(({ callback, context }) => {
      try {
        if (context) {
          callback.call(context, data);
        } else {
          callback(data);
        }
      } catch (error) {
        console.error(`Error in cart event listener for ${event}:`, error);
      }
    });
  }

  /**
   * Get cart data with caching
   */
  async getCart() {
    const now = Date.now();

    // Return cached data if still valid
    if (this.cartCache && now - this.cacheTimestamp < this.cacheTimeout) {
      return this.cartCache;
    }

    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }

      this.cartCache = await response.json();
      this.cacheTimestamp = now;

      return this.cartCache;
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error;
    }
  }

  /**
   * Clear cart cache
   */
  clearCache() {
    this.cartCache = null;
    this.cacheTimestamp = 0;
  }

  /**
   * Force cart refresh
   */
  async forceRefresh() {
    this.clearCache();
    const cart = await this.getCart();
    this.notifyListeners('cart:updated', { cart });
    return cart;
  }

  /**
   * Update cart item
   */
  async updateItem(key, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update cart item: ${response.status}`);
    }

    this.clearCache();
    this.notifyListeners('cart:item-updated', { key, quantity });

    return response.json();
  }

  /**
   * Add item to cart
   */
  async addItem(item) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item),
    });

    if (!response.ok) {
      throw new Error(`Failed to add item to cart: ${response.status}`);
    }

    this.clearCache();
    this.notifyListeners('cart:item-added', { item });

    return response.json();
  }

  /**
   * Remove item from cart
   */
  async removeItem(key) {
    return this.updateItem(key, 0);
  }

  /**
   * Get cart items from DOM
   */
  getCartItemsFromDOM() {
    const cartItems = [];

    // Main cart items
    const mainCartItems = document.querySelectorAll('#main-cart-items .cart-item');
    mainCartItems.forEach((item, index) => {
      const productTitle = item.querySelector('.cart-item__name')?.textContent?.trim();
      const quantity = parseInt(item.querySelector('input[name="updates[]"]')?.value || 0);
      const variantId = item.querySelector('input[name="updates[]"]')?.dataset?.quantityVariantId;
      const itemKey = item.getAttribute('data-cart-item-key') || item.getAttribute('data-index') || `main-${index}`;

      if (productTitle && quantity > 0) {
        cartItems.push({
          title: productTitle,
          quantity: quantity,
          variantId: variantId,
          itemKey: itemKey,
          element: item,
          isDrawer: false,
        });
      }
    });

    // Cart drawer items
    const drawerItems = document.querySelectorAll('#CartDrawer-CartItems .cart-item');
    drawerItems.forEach((item, index) => {
      const productTitle = item.querySelector('.cart-item__name')?.textContent?.trim();
      const quantity = parseInt(item.querySelector('input[name="updates[]"]')?.value || 0);
      const variantId = item.querySelector('input[name="updates[]"]')?.dataset?.quantityVariantId;
      const itemKey = item.getAttribute('data-cart-item-key') || item.getAttribute('data-index') || `drawer-${index}`;

      if (productTitle && quantity > 0) {
        // Use unique cart item key to identify each line item separately
        // This ensures products with same title but different properties (e.g., engraving) are counted separately
        const existingItem = cartItems.find((cartItem) => cartItem.itemKey === itemKey);

        if (!existingItem) {
          cartItems.push({
            title: productTitle,
            quantity: quantity,
            variantId: variantId,
            itemKey: itemKey,
            element: item,
            isDrawer: true,
          });
        }
      }
    });

    return cartItems;
  }

  /**
   * Get product tags from element
   */
  getProductTagsFromElement(element) {
    if (!element) return [];

    const tagsString = element.getAttribute('data-product-tags');
    if (tagsString) {
      return tagsString.split(',').map((tag) => tag.trim());
    }

    return [];
  }

  /**
   * Cleanup method
   */
  destroy() {
    this.listeners.clear();
    this.clearCache();

    // Clear all timeouts
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    if (this.updateRateLimit) {
      clearTimeout(this.updateRateLimit);
    }

    if (window._cartManagerFetchPatched) {
      // Note: We don't restore original fetch as other scripts might depend on our monitoring
      delete window._cartManagerFetchPatched;
    }

    delete window.CartManager;
  }
}

// Initialize Cart Manager
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartManager());
} else {
  new CartManager();
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartManager;
}
